import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PortfolioAddNewCN from '../PortfolioAddNewCN'
import PortfoliosCards from './PortfoliosCards'
import PortfoliosTable from './PortfoliosTable'
import Layout from 'components/Layout'
import Message from 'components/Message'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from 'components/ui/dialog'
import Spinner from 'components/Spinner'
import useTable from 'components/controls/useTable'
import PortfoliosEmptyState from 'components/PortfoliosEmptyState'
import { ViewToggle } from 'components/ViewToggle'
import { List, LayoutGrid, FolderOpen, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react'
import useCapabilities from 'hooks/useCapabilities'
import { createPortfolio, initializePortfolios } from 'slices/portfolioSlice'

const headerCells = [
	{ id: 'assetName', label: 'Portfolio' },
	{ id: 'description', label: 'Description' },
	{ id: 'locationCount', label: 'Locations' },
	{ id: 'owner.image', label: 'Owner', disableSorting: true },
	{ id: 'owner.name', label: '', disableSorting: true },
	{ id: 'updatedAt', label: 'Updated' },
]

const Portfolios = ({ cardDisplay, onToggleCardDisplay, isMobile, listOnly = isMobile, openPopup, setOpenPopup }) => {
	const dispatch = useDispatch()
	const { canCreateAssets } = useCapabilities()

	const searchInputRef = useRef(null)
	const [searchInput, setSearchInput] = useState('')

	// Card view sort state
	const [cardSortBy, setCardSortBy] = useState(null)
	const [cardSortOrder, setCardSortOrder] = useState('asc')

	const cardSortFields = [
		{ id: 'assetName', label: 'Name' },
		{ id: 'updatedAt', label: 'Date Updated' },
	]

	const handleCardSort = fieldId => {
		if (cardSortBy === fieldId) {
			setCardSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setCardSortBy(fieldId)
			setCardSortOrder('asc')
		}
	}

	const [filterFn, setFilterFn] = useState({
		fn: items => {
			return items
		},
	})

	const userLogin = useSelector(state => state.userLogin)
	const { userInfo: user } = userLogin
	const userId = user._id

	const { entities: portfolios, error, ids: portfolioIds, isLoading } = useSelector(state => state.portfolios)
	const newPortfolios = portfolioIds.map(id => portfolios[id])

	var records = []

	if (newPortfolios.length > 0) {
		records = newPortfolios
			.filter(item => item && item._id && item.owner) // Filter out invalid entries
			.map(item => {
				const locationImages = (item.locations || []).map(loc => loc?.location?.image).filter(Boolean)
				return {
					_id: item._id,
					assetName: item.assetName || '',
					description: item.description || '',
					portfolioType: item.portfolioType || '',
					locationCount: item.locations?.length || 0,
					locationImages,
					owner: item.owner,
					updatedAt: item.updatedAt,
				}
			})
	}

	useEffect(() => {
		// Always dispatch on mount to ensure fresh data when navigating back
		dispatch(initializePortfolios())
		// dispatch(initializeUsers())
	}, [dispatch])

	const recordCount = records.length

	const { recordsAfterPagingAndSorting } = useTable(records, headerCells, filterFn, true)

	// Sort cards
	const sortedCardPortfolios = useMemo(() => {
		const items = recordsAfterPagingAndSorting()
		if (!cardSortBy) return items
		return [...items].sort((a, b) => {
			let aVal = a[cardSortBy] ?? ''
			let bVal = b[cardSortBy] ?? ''
			if (cardSortBy === 'updatedAt') {
				aVal = new Date(aVal).getTime() || 0
				bVal = new Date(bVal).getTime() || 0
			} else {
				aVal = aVal.toString().toLowerCase()
				bVal = bVal.toString().toLowerCase()
			}
			if (aVal < bVal) return cardSortOrder === 'asc' ? -1 : 1
			if (aVal > bVal) return cardSortOrder === 'asc' ? 1 : -1
			return 0
		})
	}, [recordsAfterPagingAndSorting, cardSortBy, cardSortOrder])

	const CardSortIcon = ({ fieldId }) => {
		if (cardSortBy !== fieldId) return <ArrowUpDown className="h-3 w-3 text-gray-300" />
		return cardSortOrder === 'asc' ? (
			<ChevronUp className="h-3 w-3 text-[#0080fe]" />
		) : (
			<ChevronDown className="h-3 w-3 text-[#0080fe]" />
		)
	}

	const CardSortPills = () => (
		<div className="flex items-center gap-1.5 flex-wrap">
			<span className="text-xs text-gray-400 mr-0.5">Sort:</span>
			{cardSortFields.map(field => (
				<button
					key={field.id}
					type="button"
					onClick={() => handleCardSort(field.id)}
					className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
						cardSortBy === field.id ? 'bg-[#0080fe]/10 text-[#0080fe]' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
					}`}
				>
					{field.label}
					<CardSortIcon fieldId={field.id} />
				</button>
			))}
		</div>
	)

	const portfolioAdd = (portfolio, resetForm) => {
		dispatch(
			createPortfolio({
				owner: userId,
				assetName: portfolio.assetName,
				description: portfolio.description,
				portfolioType: portfolio.portfolioType,
				updateNote: 'New Portfolio',
			})
		)
		resetForm()
		setOpenPopup(false)
	}

	const handelPortfolioAdd = () => {
		setOpenPopup(true)
	}

	// No map on this collection, so on mobile there is exactly one legal view and
	// the toggle is not rendered at all (see customButtons below) — a one-option
	// pill group would be noise.
	const viewOptions = [
		{ value: 'cards', label: 'Cards', icon: LayoutGrid },
		{ value: 'list', label: 'List', icon: List },
	]

	// The boolean prop contract and its localStorage format are unchanged; the
	// string union is bridged here and never escapes this component.
	const renderViewToggle = () => (
		<ViewToggle
			value={cardDisplay ? 'cards' : 'list'}
			onChange={value => {
				if ((value === 'cards') !== cardDisplay) onToggleCardDisplay()
			}}
			options={viewOptions}
		/>
	)

	const handleSearchAll = e => {
		const { value } = e.target
		setSearchInput(value)
		setFilterFn({
			fn: items => {
				if (value === '') return items
				const lowerValue = value.toLowerCase()
				return items.filter(
					x =>
						(x.assetName && x.assetName.toLowerCase().includes(lowerValue)) ||
						(x.description && x.description.toLowerCase().includes(lowerValue)) ||
						(x.portfolioType && x.portfolioType.toLowerCase().includes(lowerValue))
				)
			},
		})
		setTimeout(() => {
			searchInputRef.current.focus()
		}, 0)
	}

	return (
		<div>
			<React.Fragment>
				{isLoading ? (
					<Spinner containerHeight="50vh" />
				) : !records ? (
					<Message variant="danger">{error}</Message>
				) : error ? (
					<Message variant="danger">{error}</Message>
				) : records.length === 0 ? (
					// Community/read-only accounts can't create — omit the CTA.
					<PortfoliosEmptyState onCreatePortfolio={canCreateAssets ? handelPortfolioAdd : undefined} />
				) : (
					<>
						<Layout.PageToolbarFilter
							buttonText={canCreateAssets ? 'Add Portfolio' : undefined}
							buttonOnClick={canCreateAssets ? () => setOpenPopup(true) : undefined}
							customButtons={!listOnly ? (
								<div className="flex items-center gap-3">
									{cardDisplay && <CardSortPills />}
									{renderViewToggle()}
								</div>
							) : undefined}
							searchInputLabel=""
							searchInputRef={searchInputRef}
							searchInputValue={searchInput}
							searchInputOnChange={handleSearchAll}
						/>
						{cardDisplay ? (
							<PortfoliosCards portfolios={sortedCardPortfolios} userId={userId} />
						) : (
							<PortfoliosTable portfolios={recordsAfterPagingAndSorting()} recordCount={recordCount} userId={userId} />
						)}
					</>
				)}
			</React.Fragment>
			<Dialog open={openPopup} onClose={() => setOpenPopup(false)}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
								<FolderOpen className="w-5 h-5" />
							</div>
							<div>
								<DialogTitle>New Portfolio</DialogTitle>
								<DialogDescription>Create a new portfolio for your workspace</DialogDescription>
							</div>
						</div>
					</DialogHeader>
					<div className="p-5">
						<PortfolioAddNewCN portfolioAdd={portfolioAdd} />
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default Portfolios
